import React from 'react';
import { Card, Alert } from 'react-bootstrap';
import {
  docTypeLabel, formatAmount, FULFILLMENT_DATE_SOURCE,
} from '../../Utils/billingoDocuments';
import { hasDrift } from '../../Utils/billingoPrefill';

/**
 * WHAT THIS DOCUMENT WAS PREPARED FROM, VALUE BY VALUE — and how the booking
 * has moved since.
 *
 * 282 of 295 of the office's végszámla are fully derivable from the
 * Előlegszámla that preceded them (285/285 identical partner account, 285/285
 * identical currency, 285/285 identical amount, 282/285 identical booking
 * set — production, 2026-11-02). The flow has pre-filled most of that for a
 * while; what it never did was SAY SO, so the operator had no way to tell a
 * copied value from a guess and no way to see the 4% that needs a second look.
 *
 * This renders nothing when there is no predecessor to copy from — the 10 of
 * 295 with no advance. In that case the form is still filled from the
 * booking, and claiming a source it does not have would be worse than silence.
 *
 * A HUMAN STILL PRESSES ISSUE. This panel exists to make the 4% visible
 * before that press; it never issues anything.
 *
 * `sources` is `prefillSources(...)`, `drift` is `orderLineDrift(...)` — both
 * derived by comparison, never asserted. See Utils/billingoPrefill.js.
 */
function BillingoPrefillCard({
  predecessor = null, docType = null, sources = {},
  account = null, currency = null, fulfillmentDate = '', drift = null,
}) {
  if (!predecessor) return null;

  const from = `${docTypeLabel(predecessor.type)} ${predecessor.number || ''}`.trim();
  const SOURCE_TEXT = {
    predecessor: `from ${from}`,
    partner: "the partner's saved account",
    order: "the order's currency",
    default: FULFILLMENT_DATE_SOURCE[docType] || 'the type default',
    edited: 'set on this form',
    picked: 'chosen on this form',
    none: 'not chosen yet',
  };

  const row = (label, value, sourceKey) => (
    <div className="d-flex justify-content-between align-items-baseline gap-2 py-1" data-testid="prefill-row">
      <span className="text-muted small">{label}</span>
      <span className="text-end">
        <span>{value || '—'}</span>
        <span className="text-muted small ms-2">{SOURCE_TEXT[sourceKey] || ''}</span>
      </span>
    </div>
  );

  const driftLine = (text) => (
    <li key={text} data-testid="prefill-drift-item">{text}</li>
  );
  const nameOf = (entry) => [entry.order_reference, entry.comment].filter(Boolean).join(' · ') || entry.key;

  return (
    /* Deliberately a NEUTRAL card, not border-success. Its job is to inform,
       not to reassure: the same panel renders above a Számla that is blocked
       because its advance is spent, and a green frame over a blocked document
       reads as two screens disagreeing. The one green thing here is the
       lines-match line, which is an actual check result. */
    <Card className="mb-3" data-testid="billingo-prefill">
      <Card.Body>
        <div className="small mb-2">
          <i className="fa fa-clock-rotate-left text-success me-1" />
          Prepared from
          {' '}
          <strong>{from}</strong>
        </div>

        {row('Billingo account', account?.name, sources.account)}
        {row('Currency', currency, sources.currency)}
        {row('Fulfillment date', fulfillmentDate, sources.fulfillment_date)}

        {!hasDrift(drift) && drift?.comparable && (
          <div className="small text-success mt-2" data-testid="prefill-matches">
            <i className="fa fa-check me-1" />
            {`The lines match what ${from} billed.`}
          </div>
        )}

        {!drift?.comparable && (
          <div className="small text-muted mt-2" data-testid="prefill-incomparable">
            {`${from} does not record which order lines it billed, so its lines cannot be `
             + 'compared with the booking as it stands now. Check the lines by hand.'}
          </div>
        )}

        {hasDrift(drift) && (
          <Alert variant="warning" className="py-2 mt-2 mb-0" data-testid="prefill-drift">
            <div className="fw-semibold small">
              <i className="fa fa-triangle-exclamation me-1" />
              {`The booking has changed since ${from}`}
            </div>
            <ul className="mb-0 mt-1 ps-3 small">
              {drift.added.map((item) => driftLine(
                `New since then: ${nameOf(item)} — ${formatAmount(item.unit_price * item.quantity, item.currency)}`,
              ))}
              {drift.removed.map((item) => driftLine(
                `No longer on the booking: ${nameOf(item)} — was ${formatAmount(item.gross, predecessor.currency)}`,
              ))}
              {drift.changed.map((item) => driftLine(
                `${nameOf(item)}: ${formatAmount(item.from, predecessor.currency)} → ${formatAmount(item.to, currency)}`,
              ))}
            </ul>
          </Alert>
        )}
      </Card.Body>
    </Card>
  );
}

export default BillingoPrefillCard;
